/**
 * Admin Campaign Manual Adjustments Component
 * Provides interface for making manual adjustments to campaign tracking records
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    PencilSquareIcon,
    ClockIcon,
    UserIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowPathIcon,
    DocumentTextIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import Modal from './Modal';

const CampaignManualAdjustments = ({ campaignUsageId, onAdjustmentComplete }) => {
    const { token } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [adjustmentHistory, setAdjustmentHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Form state
    const [adjustmentForm, setAdjustmentForm] = useState({
        adjustmentType: 'discount_amount',
        adjustmentValue: '',
        reason: '',
        notes: ''
    });

    const adjustmentTypes = [
        { value: 'discount_amount', label: 'İndirim Tutarı', description: 'Orijinal indirim tutarını değiştir' },
        { value: 'remaining_amount', label: 'Kalan Tutar', description: 'Kalan indirim tutarını değiştir' },
        { value: 'status', label: 'Durum', description: 'Kampanya kullanım durumunu değiştir' },
        { value: 'refund_amount', label: 'İade Tutarı', description: 'İade edilen tutarı değiştir' }
    ];

    const statusOptions = [
        { value: 'active', label: 'Aktif' },
        { value: 'refunded', label: 'İade Edildi' },
        { value: 'partial_refund', label: 'Kısmi İade' },
        { value: 'expired', label: 'Süresi Doldu' }
    ];

    const reasonOptions = [
        'Hesaplama hatası düzeltmesi',
        'Müşteri şikayeti çözümü',
        'Sistem hatası düzeltmesi',
        'İade işlemi düzeltmesi',
        'Kampanya kuralı değişikliği',
        'Özel durum - diğer'
    ];

    // Load adjustment history when modal opens
    useEffect(() => {
        if (isOpen && campaignUsageId) {
            loadAdjustmentHistory();
        }
    }, [isOpen, campaignUsageId]);

    const loadAdjustmentHistory = async () => {
        try {
            setLoadingHistory(true);
            console.log('[DEBUG] Loading adjustment history for:', campaignUsageId);
            
            const response = await api.get(`/api/admin/campaign-adjustments/history/${campaignUsageId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('[DEBUG] API Response:', response.data);

            // API yanıtı kontrolü - hem success field'ı hem de direkt array kontrolü
            let adjustments = [];
            
            if (response.data.success && response.data.data) {
                // Normal API yanıtı: {success: true, data: [...]}
                adjustments = Array.isArray(response.data.data) ? response.data.data : [];
                console.log('[DEBUG] Using response.data.data format');
            } else if (Array.isArray(response.data)) {
                // Direkt array yanıtı: [...]
                adjustments = response.data;
                console.log('[DEBUG] Using direct array format');
            } else {
                console.error('[DEBUG] Unexpected API response format:', response.data);
                adjustments = [];
            }
            
            console.log('[DEBUG] Final adjustments:', adjustments);
            console.log('[DEBUG] Adjustments count:', adjustments.length);
            
            setAdjustmentHistory(adjustments);
            
            // Force re-render check
            setTimeout(() => {
                console.log('[DEBUG] State after set:', adjustments.length, 'items');
            }, 100);
        } catch (error) {
            console.error('[DEBUG] Adjustment history load error:', error);
            toast.error('Düzeltme geçmişi yüklenemedi');
            setAdjustmentHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!adjustmentForm.adjustmentValue || !adjustmentForm.reason) {
            toast.error('Değer ve sebep alanları zorunludur');
            return;
        }

        try {
            setLoading(true);
            
            const requestData = {
                campaignUsageId,
                adjustmentType: adjustmentForm.adjustmentType,
                adjustmentValue: adjustmentForm.adjustmentValue,
                reason: adjustmentForm.reason,
                notes: adjustmentForm.notes
            };

            const response = await api.post('/api/admin/campaign-adjustments/manual-adjustment', requestData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success('Manuel düzeltme başarıyla uygulandı');
                
                // Reset form
                setAdjustmentForm({
                    adjustmentType: 'discount_amount',
                    adjustmentValue: '',
                    reason: '',
                    notes: ''
                });

                // Reload history
                console.log('[DEBUG] Reloading adjustment history after successful adjustment...');
                await loadAdjustmentHistory();
                
                // Notify parent component
                if (onAdjustmentComplete) {
                    onAdjustmentComplete(response.data.data);
                }
            }
        } catch (error) {
            console.error('Manual adjustment error:', error);
            const errorMessage = error.response?.data?.error?.message || 'Düzeltme uygulanırken hata oluştu';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleRevertAdjustment = async (adjustmentId) => {
        const reason = prompt('Geri alma sebebini giriniz:');
        if (!reason) return;

        try {
            const response = await api.post(`/api/admin/campaign-adjustments/revert/${adjustmentId}`, {
                reason
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success('Düzeltme başarıyla geri alındı');
                await loadAdjustmentHistory();
                
                if (onAdjustmentComplete) {
                    onAdjustmentComplete({ reverted: true });
                }
            }
        } catch (error) {
            console.error('Revert adjustment error:', error);
            const errorMessage = error.response?.data?.error?.message || 'Düzeltme geri alınırken hata oluştu';
            toast.error(errorMessage);
        }
    };

    const getAdjustmentTypeLabel = (type) => {
        const typeLabels = {
            'discount_amount': 'İndirim Tutarı',
            'original_discount_amount': 'Orijinal İndirim Tutarı',
            'remaining_amount': 'Kalan Tutar',
            'remaining_discount_amount': 'Kalan İndirim Tutarı',
            'status': 'Durum',
            'refund_amount': 'İade Tutarı',
            'metadata': 'Meta Veri',
            'notes': 'Notlar'
        };
        
        return typeLabels[type] || type;
    };

    const formatAdjustmentValue = (type, value) => {
        if (type === 'discount_amount' || type === 'remaining_amount' || type === 'refund_amount') {
            return `₺${parseFloat(value).toFixed(2)}`;
        }
        if (type === 'status') {
            const statusObj = statusOptions.find(s => s.value === value);
            return statusObj?.label || value;
        }
        return value;
    };

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center space-x-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                title="Manuel Düzeltme"
            >
                <PencilSquareIcon className="h-4 w-4" />
                <span>Düzelt</span>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
                        <div className="bg-white rounded-xl max-w-4xl w-full mx-auto max-h-[90vh] overflow-hidden">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <ShieldCheckIcon className="h-8 w-8" />
                                        <div>
                                            <h3 className="text-xl font-bold">Manuel Kampanya Düzeltmesi</h3>
                                            <p className="text-orange-100">Kampanya kullanım kaydında manuel değişiklik yapın</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="flex h-[600px]">
                                {/* Left Side - Adjustment Form */}
                                <div className="w-1/2 p-6 border-r border-gray-200">
                                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                        <PencilSquareIcon className="h-5 w-5 mr-2 text-orange-500" />
                                        Yeni Düzeltme
                                    </h4>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        {/* Adjustment Type */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Düzeltme Türü
                                            </label>
                                            <select
                                                value={adjustmentForm.adjustmentType}
                                                onChange={(e) => setAdjustmentForm(prev => ({
                                                    ...prev,
                                                    adjustmentType: e.target.value,
                                                    adjustmentValue: '' // Reset value when type changes
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            >
                                                {adjustmentTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {adjustmentTypes.find(t => t.value === adjustmentForm.adjustmentType)?.description}
                                            </p>
                                        </div>

                                        {/* Adjustment Value */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Yeni Değer
                                            </label>
                                            {adjustmentForm.adjustmentType === 'status' ? (
                                                <select
                                                    value={adjustmentForm.adjustmentValue}
                                                    onChange={(e) => setAdjustmentForm(prev => ({
                                                        ...prev,
                                                        adjustmentValue: e.target.value
                                                    }))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                    required
                                                >
                                                    <option value="">Durum seçin</option>
                                                    {statusOptions.map(status => (
                                                        <option key={status.value} value={status.value}>
                                                            {status.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : adjustmentForm.adjustmentType === 'metadata' ? (
                                                <textarea
                                                    value={adjustmentForm.adjustmentValue}
                                                    onChange={(e) => setAdjustmentForm(prev => ({
                                                        ...prev,
                                                        adjustmentValue: e.target.value
                                                    }))}
                                                    placeholder="JSON formatında metadata giriniz"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-20 resize-none"
                                                    required
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={adjustmentForm.adjustmentValue}
                                                    onChange={(e) => setAdjustmentForm(prev => ({
                                                        ...prev,
                                                        adjustmentValue: e.target.value
                                                    }))}
                                                    placeholder="Tutar giriniz"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                    required
                                                />
                                            )}
                                        </div>

                                        {/* Reason */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Düzeltme Sebebi *
                                            </label>
                                            <select
                                                value={adjustmentForm.reason}
                                                onChange={(e) => setAdjustmentForm(prev => ({
                                                    ...prev,
                                                    reason: e.target.value
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                required
                                            >
                                                <option value="">Sebep seçin</option>
                                                {reasonOptions.map(reason => (
                                                    <option key={reason} value={reason}>
                                                        {reason}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Notes */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Ek Notlar
                                            </label>
                                            <textarea
                                                value={adjustmentForm.notes}
                                                onChange={(e) => setAdjustmentForm(prev => ({
                                                    ...prev,
                                                    notes: e.target.value
                                                }))}
                                                placeholder="Ek açıklamalar yazabilirsiniz..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-16 resize-none"
                                            />
                                        </div>

                                        {/* Submit Button */}
                                        <div className="pt-2">
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {loading ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                ) : (
                                                    <>
                                                        <CheckCircleIcon className="h-4 w-4" />
                                                        <span>Düzeltmeyi Uygula</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                {/* Right Side - Adjustment History */}
                                <div className="w-1/2 p-6 bg-gray-50">
                                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                        <ClockIcon className="h-5 w-5 mr-2 text-blue-500" />
                                        Düzeltme Geçmişi
                                    </h4>

                                    <div className="h-[500px] overflow-y-auto">
                                        {loadingHistory ? (
                                            <div className="flex items-center justify-center h-32">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                <span className="ml-3 text-gray-600">Yükleniyor...</span>
                                            </div>
                                        ) : adjustmentHistory.length === 0 ? (
                                            <div className="text-center text-gray-500 py-8">
                                                <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                                <p>Henüz düzeltme geçmişi yok</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {adjustmentHistory.map((adjustment, index) => (
                                                    <motion.div
                                                        key={adjustment.id}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: index * 0.05 }}
                                                        className={`p-4 rounded-lg border ${
                                                            adjustment.reverted_at 
                                                                ? 'bg-red-50 border-red-200' 
                                                                : 'bg-white border-gray-200'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        {getAdjustmentTypeLabel(adjustment.adjustment_type)}
                                                                    </span>
                                                                    {adjustment.reverted_at && (
                                                                        <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                                                            Geri Alındı
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="text-xs text-gray-600 space-y-1">
                                                                    <div>
                                                                        <span className="font-medium">Eski:</span> {formatAdjustmentValue(adjustment.adjustment_type, adjustment.original_value)}
                                                                        {' → '}
                                                                        <span className="font-medium">Yeni:</span> {formatAdjustmentValue(adjustment.adjustment_type, adjustment.adjusted_value)}
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-medium">Sebep:</span> {adjustment.reason}
                                                                    </div>
                                                                    {adjustment.notes && (
                                                                        <div>
                                                                            <span className="font-medium">Not:</span> {adjustment.notes}
                                                                        </div>
                                                                    )}
                                                                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                                        <span className="flex items-center">
                                                                            <UserIcon className="h-3 w-3 mr-1" />
                                                                            {adjustment.admin_name}
                                                                        </span>
                                                                        <span>
                                                                            {new Date(adjustment.created_at).toLocaleString('tr-TR')}
                                                                        </span>
                                                                    </div>
                                                                    {adjustment.reverted_at && (
                                                                        <div className="text-xs text-red-600 mt-2">
                                                                            <span className="font-medium">Geri alma sebebi:</span> {adjustment.revert_reason}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            {!adjustment.reverted_at && (
                                                                <button
                                                                    onClick={() => handleRevertAdjustment(adjustment.id)}
                                                                    className="ml-2 p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                                                    title="Düzeltmeyi Geri Al"
                                                                >
                                                                    <ArrowPathIcon className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </>
    );
};

export default CampaignManualAdjustments;